import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import React from 'react'
import { useForm } from '@fuse/hooks'
import { Link } from 'react-router-dom'
import apiService from 'app/services/apiService'
import history from '@history'
import { bindActionCreators } from '@reduxjs/toolkit'
import { hideMessage, showMessage } from 'app/store/fuse/messageSlice'
import { connect } from 'react-redux'
import { setCookie } from 'tiny-cookie'
import bcrypt from 'bcryptjs'

// Password Security Indicator Component
const PasswordStrengthIndicator = ({ password, previousHashes = [] }) => {
	const getPasswordStrength = password => {
		if (!password) return { score: 0, label: '', color: '#e0e0e0' }

		let score = 0
		let feedback = []

		// Length check
		if (password.length >= 8) score += 1
		else feedback.push('At least 8 characters')

		// Special character check
		if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1
		else feedback.push('Include a special character')

		// Uppercase check
		if (/[A-Z]/.test(password)) score += 1
		else feedback.push('Include uppercase letter')

		// Lowercase check
		if (/[a-z]/.test(password)) score += 1
		else feedback.push('Include lowercase letter')

		// Number check
		if (/[0-9]/.test(password)) score += 1
		else feedback.push('Include a number')

		// Previous password check - using bcrypt.compare
		const isNotPreviousPassword = !previousHashes.some(hash => bcrypt.compareSync(password, hash))
		if (isNotPreviousPassword) score += 1
		else feedback.push('Password must be different from previous passwords')

		const strengthMap = {
			0: { label: 'Very Weak', color: '#f44336' },
			1: { label: 'Weak', color: '#ff9800' },
			2: { label: 'Fair', color: '#ffc107' },
			3: { label: 'Good', color: '#4caf50' },
			4: { label: 'Strong', color: '#2e7d32' },
			5: { label: 'Very Strong', color: '#1b5e20' },
			6: { label: 'Excellent', color: '#0d47a1' }
		}

		return {
			score,
			label: strengthMap[score]?.label || 'Very Weak',
			color: strengthMap[score]?.color || '#f44336',
			feedback
		}
	}

	const strength = getPasswordStrength(password)
	const segments = 6
	const filledSegments = strength.score

	return (
		<div className="mb-16">
			<div className="flex items-center justify-between mb-8">
				<span className="text-sm font-medium">Password Strength:</span>
				<span className="text-sm" style={{ color: strength.color }}>
					{strength.label}
				</span>
			</div>
			<div className="flex gap-1 mb-8">
				{Array.from({ length: segments }, (_, index) => (
					<div
						key={index}
						className="h-2 flex-1 rounded"
						style={{
							backgroundColor: index < filledSegments ? strength.color : '#e0e0e0'
						}}
					/>
				))}
			</div>
			{password && strength.feedback.length > 0 && (
				<div className="text-xs text-gray-600">
					{strength.feedback.map((item, index) => (
						<div key={index} className="flex items-center">
							<span className="mr-1">•</span>
							{item}
						</div>
					))}
				</div>
			)}
		</div>
	)
}

const Form = props => {
	const { form, handleChange, resetForm } = useForm({
		password: '',
		passwordConfirm: ''
	})

	const isFormValid = () => {
		const hasMinLength = form.password.length >= 8
		const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(form.password)
		const passwordsMatch = form.password === form.passwordConfirm

		// Check against previous passwords using bcrypt.compare
		const previousHashes = props.data?.hashes || []
		const isNotPreviousPassword = !previousHashes.some(hash => bcrypt.compareSync(form.password, hash))

		return hasMinLength && hasSpecialChar && passwordsMatch && isNotPreviousPassword
	}

	const handleSubmit = ev => {
		ev.preventDefault()
		apiService
			.resetPassword(form.password, props.data.UserID)
			.then(response => {
				props.showMessage({ message: response.message })
				if (response.applications.indexOf('FiberUS') > -1) {
					setCookie('identifier', 'fiberus')
				} else if (response.applications.indexOf('Fiber') > -1) {
					setCookie('identifier', 'fiber')
				} else if (response.applications.indexOf('FormsUS') > -1) {
					setCookie('identifier', 'formsus')
				} else if (response.applications.indexOf('Forms') > -1) {
					setCookie('identifier', 'forms')
				} else if (response.applications.indexOf('Pulse') > -1) {
					setCookie('identifier', 'pulse')
				}
				history.push({
					pathname: '/login'
				})
			})
			.catch(error => {})
		resetForm()
	}

	return (
		<React.Fragment>
			<form name="resetForm" noValidate className="flex flex-col justify-center w-full" onSubmit={handleSubmit}>
				<TextField
					className="mb-16"
					label="Password"
					type="password"
					name="password"
					value={form.password}
					onChange={handleChange}
					variant="outlined"
					required
					fullWidth
				/>

				<PasswordStrengthIndicator password={form.password} previousHashes={props.data?.hashes || []} />

				<TextField
					className="mb-16"
					label="Password (Confirm)"
					type="password"
					name="passwordConfirm"
					value={form.passwordConfirm}
					onChange={handleChange}
					variant="outlined"
					required
					fullWidth
				/>

				<Button
					variant="contained"
					color="primary"
					className="w-224 mx-auto mt-16"
					aria-label="Reset"
					disabled={!isFormValid()}
					type="submit"
				>
					SETUP MY PASSWORD
				</Button>
			</form>
			<div className="flex flex-col items-center justify-center pt-32 pb-24">
				<Link className="font-medium" to="/login">
					Go back to login
				</Link>
			</div>
		</React.Fragment>
	)
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators(
		{
			showMessage,
			hideMessage
		},
		dispatch
	)
}

export default connect(null, mapDispatchToProps)(Form)
